(() => {
  'use strict';
  if (window.NiralaProductInitialized) return;
  window.NiralaProductInitialized = true;
  const refreshByForm = new WeakMap();

  const plainText = (value) => {
    const template = document.createElement('template');
    template.innerHTML = String(value || '');
    return template.content.textContent || '';
  };

  function initialize(scope = document) {
    scope.querySelectorAll('[data-product-section]').forEach((section) => {
      if (section.dataset.productReady) return;
      section.dataset.productReady = 'true';
      const form = section.querySelector('[data-product-form]');
      if (!form) return;
      const variantInput = form.elements.namedItem('id');
      const addButton = section.querySelector('[data-add-to-cart]');
      const label = addButton?.querySelector('[data-add-label]');
      const error = section.querySelector('[data-product-error]');
      const success = section.querySelector('[data-product-success]');
      const image = section.querySelector('[data-product-image]');
      const initialAvailable = addButton ? !addButton.disabled : false;
      let variants = [];
      let pending = false;
      let optionsEnhanced = false;
      let choicesComplete = true;
      let displayedVariant = null;
      const picker = section.querySelector('[data-product-picker]');
      const optionContainer = section.querySelector('[data-product-options]');
      const optionGroups = [...section.querySelectorAll('[data-product-option-group]')];
      const selectedOptions = optionGroups.map(() => null);
      try {
        variants = JSON.parse(section.querySelector('[data-product-variants]')?.textContent || '[]');
      } catch (_) {
        // Native Shopify product form remains available if this payload is missing.
      }

      const chosen = () => variants.find((variant) => String(variant.id) === variantInput?.value);
      const quantityValue = () => Number(form.elements.namedItem('quantity')?.value || 1);
      const addonsValid = () => typeof window.NiralaProductAddons?.isValid !== 'function' || window.NiralaProductAddons.isValid(form, quantityValue());
      const updateButton = () => {
        if (!addButton) return;
        const selected = chosen();
        const available = selected ? selected.available : optionsEnhanced ? false : initialAvailable;
        if (choicesComplete && (selected ? !selected.available : !optionsEnhanced && !initialAvailable)) addButton.dataset.soldOut = 'true';
        else delete addButton.dataset.soldOut;
        window.NiralaBusyControls.set(addButton, pending, !available || !addonsValid());
        if (label) label.textContent = !choicesComplete || available ? addButton.dataset.addLabel : addButton.dataset.soldLabel;
        const quantity = form.elements.namedItem('quantity');
        section.querySelectorAll('[data-quantity-change]').forEach(button => {
          const current = Number(quantity?.value || 1);
          const min = Number(quantity?.min) || 1;
          const step = Number(quantity?.step) || 1;
          const max = quantity?.max ? Number(quantity.max) : Infinity;
          const unavailable = Number(button.dataset.quantityChange) < 0 ? current <= min : current + step > max;
          window.NiralaBusyControls.set(button, pending, unavailable, false);
        });
        if (quantity) quantity.readOnly = pending;
      };
      refreshByForm.set(form, updateButton);
      const notifyProductUpdate = () => {
        if (optionsEnhanced) section.dataset.optionsReady = String(choicesComplete);
        section.dispatchEvent(new CustomEvent('nirala:product-updated', { bubbles: true, detail: { form, variant: chosen() || null, previewVariant: displayedVariant, quantity: quantityValue() } }));
        updateButton();
      };

      const showImage = (url, alt) => {
        if (!image || !url) return;
        // Keep responsive image candidates when the selected photo is already shown.
        try { if (new URL(image.src, location.href).pathname === new URL(url, location.href).pathname) return; } catch (_) { /* Let the browser resolve the source. */ }
        image.src = url;
        image.removeAttribute('srcset');
        if (alt) image.alt = alt;
        section.querySelectorAll('[data-product-thumbnail]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.imageUrl === url));
        });
      };

      const renderVariant = (selected, commit = true) => {
        if (!selected) { notifyProductUpdate(); return; }
        displayedVariant = selected;
        section.dataset.displayVariant = String(selected.id);
        const price = section.querySelector('[data-product-price]');
        const compare = section.querySelector('[data-product-compare]');
        if (price) price.textContent = plainText(selected.price);
        if (compare) {
          compare.textContent = plainText(selected.compare);
          compare.hidden = !selected.compare;
          compare.setAttribute('aria-label', `${compare.dataset.regularPriceLabel || 'Regular price'} ${plainText(selected.compare)}`.trim());
        }
        const badge = section.querySelector('[data-product-sale-badge]');
        if (badge) {
          badge.textContent = selected.saleBadgeText || '';
          badge.hidden = !selected.saleBadgeText;
        }
        if (commit) {
          showImage(selected.image);
          const quantity = form.elements.namedItem('quantity');
          if (quantity) {
            const min = Number(selected.min) || 1;
            const step = Number(selected.step) || 1;
            quantity.min = String(min); quantity.step = String(step);
            if (selected.max != null) quantity.max = String(selected.max);
            else quantity.removeAttribute('max');
            const value = Number(quantity.value) || min;
            if (value < min || (value - min) % step || (selected.max != null && value > Number(selected.max))) quantity.value = String(min);
          }
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(selected.id));
          window.history.replaceState({}, '', url);
          section.dispatchEvent(new CustomEvent('nirala:variant-change', { bubbles: true, detail: { variant: selected } }));
        }
        if (error) error.hidden = true;
        if (success) success.hidden = true;
        notifyProductUpdate();
      };
      const updateChoices = () => {
        choicesComplete = selectedOptions.every(value => value !== null);
        optionGroups.forEach((group, index) => {
          const ready = selectedOptions.slice(0, index).every(value => value !== null);
          const fieldset = group.querySelector('[data-product-option-index]');
          fieldset.disabled = !ready;
          group.dataset.confirmed = String(selectedOptions[index] !== null);
          group.querySelector('[data-product-selected-value]').textContent = selectedOptions[index] || '';
          group.querySelector('[data-product-option-status]').textContent = selectedOptions[index] !== null ? picker.dataset.selectedLabel || 'Selected' : picker.dataset.requiredLabel || 'Required';
          group.querySelectorAll('[data-product-option-value]').forEach(input => {
            const available = variants.some(variant => variant.available && variant.options[index] === input.value && selectedOptions.slice(0, index).every((value, previous) => value === null || variant.options[previous] === value));
            input.disabled = !ready || !available;
            input.checked = input.value === selectedOptions[index];
            input.closest('label').dataset.unavailable = String(!ready || !available);
          });
        });
        const selected = choicesComplete ? variants.find(variant => variant.options.every((value, index) => value === selectedOptions[index])) : null;
        // Only the exact, fully chosen combination can be submitted to Shopify.
        variantInput.value = selected ? String(selected.id) : '';
        if (selected) renderVariant(selected);
        else {
          const possible = variants.filter(variant => variant.available && selectedOptions.every((value, index) => value === null || variant.options[index] === value));
          const preview = possible.reduce((lowest, variant) => !lowest || variant.priceAmount < lowest.priceAmount ? variant : lowest, null);
          if (preview) renderVariant(preview, false);
          else notifyProductUpdate();
          const url = new URL(window.location.href);
          url.searchParams.delete('variant');
          window.history.replaceState({}, '', url);
        }
      };
      const canEnhance = optionContainer && variantInput && optionGroups.length && variants.length && variants.every(variant => Array.isArray(variant.options) && variant.options.length === optionGroups.length);
      if (canEnhance) {
        optionsEnhanced = true;
        const explicitVariant = section.dataset.preselectedVariant || new URL(window.location.href).searchParams.get('variant');
        const preselected = variants.find(variant => String(variant.id) === explicitVariant);
        if (preselected) preselected.options.forEach((value, index) => { selectedOptions[index] = value; });
        optionContainer.hidden = false;
        const fallback = section.querySelector('[data-product-native-picker]');
        if (fallback) fallback.hidden = true;
        optionGroups.forEach(group => {
          group.open = false;
          group.querySelector('summary')?.addEventListener('click', () => {
            optionGroups.forEach(other => { if (other !== group) other.open = false; });
          });
          group.addEventListener('keydown', event => {
            if (event.key === 'Escape') {
              event.preventDefault(); event.stopPropagation(); group.open = false;
              group.querySelector('summary')?.focus({ preventScroll: true });
            }
          });
        });
        document.addEventListener('click', event => {
          if (!optionContainer.contains(event.target)) optionGroups.forEach(group => { group.open = false; });
        });
        optionGroups.forEach((group, index) => {
          group.addEventListener('change', event => {
            if (!event.target.matches('[data-product-option-value]') || event.target.disabled || !event.target.checked) return;
            selectedOptions[index] = event.target.value;
            // A different crust must never silently reuse the old size or variant.
            for (let next = index + 1; next < selectedOptions.length; next += 1) selectedOptions[next] = null;
            updateChoices();
            if (picker.dataset.autoAdvance !== 'false') {
              group.open = false;
              const next = optionGroups[index + 1];
              if (next) {
                next.open = true;
                next.querySelector('summary')?.focus({ preventScroll: true });
                // Keep the gallery and viewport stable when the next accordion opens.
              } else {
                const addons = section.querySelector('[data-addons-panel]');
                if (addons && choicesComplete) addons.open = true;
              }
            }
          });
        });
        updateChoices();
      }
      variantInput?.addEventListener('change', () => {
        const selected = chosen();
        if (optionsEnhanced && selected) {
          selected.options.forEach((value, index) => { selectedOptions[index] = value; });
          updateChoices();
        } else renderVariant(selected);
      });

      section.querySelectorAll('[data-product-thumbnail]').forEach((button) => {
        button.addEventListener('click', () => showImage(button.dataset.imageUrl, button.dataset.imageAlt));
      });

      section.querySelectorAll('[data-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          if (pending || button.disabled) return;
          const quantity = form.elements.namedItem('quantity');
          if (!quantity) return;
          const min = Number(quantity.min) || 1;
          const step = Number(quantity.step) || 1;
          const max = quantity.max ? Number(quantity.max) : Infinity;
          const current = Number.parseInt(quantity.value, 10) || min;
          quantity.value = String(Math.min(max, Math.max(min, current + Number(button.dataset.quantityChange) * step)));
          quantity.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      const quantityInput = form.elements.namedItem('quantity');
      quantityInput?.addEventListener('input', notifyProductUpdate);
      quantityInput?.addEventListener('change', notifyProductUpdate);
      window.NiralaProductAddons?.mount?.(form);
      notifyProductUpdate();

      form.addEventListener('submit', async (event) => {
        if (pending) { event.preventDefault(); return; }
        if (!choicesComplete || (variants.length && !chosen())) {
          event.preventDefault();
          if (error) { error.textContent = section.dataset.invalidVariant; error.hidden = false; }
          return;
        }
        if ((window.NiralaIncludedOptions && !window.NiralaIncludedOptions.validate(form)) || !addonsValid()) { event.preventDefault(); return; }
        // Preserve an ordinary Shopify form submit when AJAX is unavailable.
        if (typeof window.Nirala?.addToCart !== 'function') return;
        event.preventDefault();
        if (!form.reportValidity()) return;
        if (chosen() && !chosen().available) return;
        pending = true;
        if (error) error.hidden = true;
        if (success) success.hidden = true;
        updateButton();
        try {
          window.NiralaProductAddons?.setBusy?.(form, true);
          const result = await window.Nirala.addToCart(form);
          if (result === false || result?.cancelled) return;
          if (result?.error) throw new Error(result.message || result.error);
          if (success) {
            success.textContent = section.dataset.addedMessage;
            success.hidden = false;
          }
        } catch (failure) {
          if (error) {
            error.textContent = failure?.message || section.dataset.errorMessage;
            error.hidden = false;
          }
        } finally {
          pending = false;
          window.NiralaProductAddons?.setBusy?.(form, false);
          updateButton();
        }
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('nirala:addons-changed', (event) => refreshByForm.get(event.detail?.form)?.());
})();
